import { useState, useEffect, useCallback } from 'react';
import {
  Loader2, Send, ArrowLeftCircle, CheckCircle2, AlertTriangle,
  ClipboardList, MessageSquare, Plus,
} from 'lucide-react';
import { useAuthStore } from '../stores/auth';
import { useToastStore } from '../stores/toast';
import {
  getCaseWorkflow, submitCase, decideStep, returnCase,
  getChecklist, updateChecklistItem,
  getNotes, addNote,
  type CaseWorkflow, type ChecklistItem, type CaseNote,
} from '../api/workflow';

interface Props {
  caseId: string;
  caseStatus: string;
  onStatusChange?: () => void;
}

const STEP_STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  in_review: 'In review',
  approved: 'Approved',
  returned: 'Returned',
  skipped: 'Skipped',
};

function stepBadgeClass(status: string): string {
  const map: Record<string, string> = {
    pending: 'bg-slate-100 text-slate-600',
    in_review: 'bg-blue-100 text-blue-700',
    approved: 'bg-green-100 text-green-700',
    returned: 'bg-amber-100 text-amber-700',
    skipped: 'bg-slate-100 text-slate-400',
  };
  return map[status] || 'bg-slate-100 text-slate-600';
}

function checklistBadgeClass(status: string): string {
  const map: Record<string, string> = {
    required: 'bg-slate-100 text-slate-600',
    provided: 'bg-green-100 text-green-700',
    missing: 'bg-red-100 text-red-700',
    requested: 'bg-amber-100 text-amber-700',
  };
  return map[status] || 'bg-slate-100 text-slate-600';
}

export default function WorkflowPanel({ caseId, caseStatus, onStatusChange }: Props) {
  const { user } = useAuthStore();
  const { addToast } = useToastStore();
  const role = user?.role || 'analyst';

  const [workflow, setWorkflow] = useState<CaseWorkflow | null>(null);
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [notes, setNotes] = useState<CaseNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const [returnMissing, setReturnMissing] = useState<string[]>([]);
  const [showReturn, setShowReturn] = useState(false);
  const [noteBody, setNoteBody] = useState('');

  const load = useCallback(async () => {
    try {
      const [wf, cl, ns] = await Promise.all([
        getCaseWorkflow(caseId),
        getChecklist(caseId),
        getNotes(caseId),
      ]);
      setWorkflow(wf);
      setChecklist(cl);
      setNotes(ns);
    } catch {
      // workflow not yet initialized for this case - that's fine.
      setWorkflow(null);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    setBusy(true);
    try {
      const wf = await submitCase(caseId);
      setWorkflow(wf);
      addToast('Case submitted for review', 'success');
      onStatusChange?.();
    } catch {
      addToast('Failed to submit case', 'error');
    } finally { setBusy(false); }
  };

  const handleApprove = async (stepId: string) => {
    setBusy(true);
    try {
      const wf = await decideStep(caseId, stepId, 'approve');
      setWorkflow(wf);
      addToast('Step approved', 'success');
      onStatusChange?.();
    } catch {
      addToast('Failed to approve step', 'error');
    } finally { setBusy(false); }
  };

  const handleEscalate = async (stepId: string) => {
    setBusy(true);
    try {
      const wf = await decideStep(caseId, stepId, 'escalate');
      setWorkflow(wf);
      addToast('Case escalated', 'success');
      onStatusChange?.();
    } finally { setBusy(false); }
  };

  const handleReturn = async () => {
    if (!returnReason.trim()) {
      addToast('A return reason is required', 'error');
      return;
    }
    setBusy(true);
    try {
      const wf = await returnCase(caseId, returnReason, returnMissing);
      setWorkflow(wf);
      setShowReturn(false);
      setReturnReason('');
      setReturnMissing([]);
      addToast('Case returned to analyst', 'success');
      onStatusChange?.();
    } catch {
      addToast('Failed to return case', 'error');
    } finally { setBusy(false); }
  };

  const handleChecklistUpdate = async (
    itemId: string, status: ChecklistItem['status'],
  ) => {
    try {
      const updated = await updateChecklistItem(caseId, itemId, { status });
      setChecklist(checklist.map((i) => (i.id === itemId ? updated : i)));
    } catch {
      addToast('Failed to update checklist item', 'error');
    }
  };

  const handleAddNote = async () => {
    if (!noteBody.trim()) return;
    try {
      const note = await addNote(caseId, noteBody);
      setNotes([...notes, note]);
      setNoteBody('');
    } catch {
      addToast('Failed to add note', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm py-4">
        <Loader2 size={16} className="animate-spin" /> Loading workflow...
      </div>
    );
  }

  const canSubmit = role === 'analyst' || role === 'principal';
  const canReview = role === 'reviewer' || role === 'principal';
  const submittable = caseStatus === 'DRAFT' || caseStatus === 'RETURNED';
  const reviewable = caseStatus === 'SUBMITTED' || caseStatus === 'IN_REVIEW';

  const currentStep = workflow?.steps.find(
    (s) => s.order === workflow.current_step_order,
  );

  return (
    <div className="space-y-6">
      {/* Workflow stepper */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700">
            Review Workflow
            {workflow && (
              <span className="ml-2 text-xs font-normal text-slate-400">
                {workflow.workflow.name}
              </span>
            )}
          </h3>
          <div className="flex gap-2">
            {canSubmit && submittable && (
              <button
                onClick={handleSubmit}
                disabled={busy}
                className="btn-primary text-sm flex items-center gap-1"
              >
                <Send size={14} /> Submit
              </button>
            )}
          </div>
        </div>

        {workflow && workflow.steps.length > 0 ? (
          <div className="flex items-center gap-1 overflow-x-auto pb-2">
            {workflow.steps.map((step, idx) => {
              const cfg = workflow.workflow.steps.find(
                (c) => c.id === step.step_config_id,
              );
              const isCurrent = step.order === workflow.current_step_order;
              return (
                <div key={step.id} className="flex items-center">
                  <div
                    className={`flex flex-col items-start px-3 py-2 rounded-lg border ${
                      isCurrent
                        ? 'border-primary-400 bg-primary-50'
                        : 'border-slate-200 bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-slate-400">
                        {step.order}
                      </span>
                      <span className="text-sm font-medium text-slate-700">
                        {cfg?.name || step.step_config_id}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded ${stepBadgeClass(step.status)}`}>
                        {STEP_STATUS_LABELS[step.status] || step.status}
                      </span>
                      {cfg && (
                        <span className="text-xs text-slate-400">
                          {cfg.required_role} ({step.approvals.filter((a) => a.decision === 'approve').length}
                          /{cfg.required_approvals})
                        </span>
                      )}
                    </div>
                    {isCurrent && canReview && reviewable && (
                      <div className="flex gap-1 mt-2">
                        <button
                          onClick={() => handleApprove(step.id)}
                          disabled={busy}
                          className="text-xs px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700 flex items-center gap-1"
                        >
                          <CheckCircle2 size={12} /> Approve
                        </button>
                        <button
                          onClick={() => setShowReturn(!showReturn)}
                          className="text-xs px-2 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 flex items-center gap-1"
                        >
                          <ArrowLeftCircle size={12} /> Return
                        </button>
                        <button
                          onClick={() => handleEscalate(step.id)}
                          disabled={busy}
                          className="text-xs px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700 flex items-center gap-1"
                        >
                          <AlertTriangle size={12} /> Escalate
                        </button>
                      </div>
                    )}
                  </div>
                  {idx < workflow.steps.length - 1 && (
                    <div className="w-6 h-px bg-slate-300 mx-1" />
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-slate-500">
            No workflow started yet. Submit the case to begin the review
            workflow.
          </p>
        )}

        {/* Return panel */}
        {showReturn && currentStep && (
          <div className="mt-3 p-3 rounded-lg border border-amber-200 bg-amber-50">
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              Return reason
            </label>
            <textarea
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              rows={2}
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm mb-2"
              placeholder="Explain what the analyst needs to fix..."
            />
            {checklist.length > 0 && (
              <div className="mb-2">
                <p className="text-xs font-semibold text-slate-600 mb-1">
                  Mark items as missing:
                </p>
                <div className="flex flex-wrap gap-2">
                  {checklist.map((item) => (
                    <label
                      key={item.id}
                      className="flex items-center gap-1 text-xs text-slate-700"
                    >
                      <input
                        type="checkbox"
                        checked={returnMissing.includes(item.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setReturnMissing([...returnMissing, item.id]);
                          } else {
                            setReturnMissing(
                              returnMissing.filter((id) => id !== item.id),
                            );
                          }
                        }}
                      />
                      {item.label}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={handleReturn}
                disabled={busy}
                className="btn-primary text-xs"
              >
                Confirm return
              </button>
              <button
                onClick={() => setShowReturn(false)}
                className="btn-secondary text-xs"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Checklist */}
      {checklist.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
            <ClipboardList size={16} /> Checklist
          </h3>
          <div className="space-y-2">
            {checklist.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between p-2 rounded-lg bg-slate-50 border border-slate-100"
              >
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-700">
                    {item.label}
                  </p>
                  {item.notes && (
                    <p className="text-xs text-slate-500 mt-0.5">
                      {item.notes}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${checklistBadgeClass(item.status)}`}>
                    {item.status}
                  </span>
                  {(role === 'analyst' || role === 'principal') && (
                    <select
                      value={item.status}
                      onChange={(e) =>
                        handleChecklistUpdate(
                          item.id,
                          e.target.value as ChecklistItem['status'],
                        )
                      }
                      className="text-xs rounded border border-slate-300 px-1 py-0.5"
                    >
                      <option value="required">required</option>
                      <option value="provided">provided</option>
                      <option value="missing">missing</option>
                      <option value="requested">requested</option>
                    </select>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notes (append-only) */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
          <MessageSquare size={16} /> Notes
        </h3>
        <div className="space-y-2 mb-3">
          {notes.length === 0 && (
            <p className="text-sm text-slate-400">No notes yet.</p>
          )}
          {notes.map((note) => (
            <div
              key={note.id}
              className="p-2 rounded-lg bg-slate-50 border border-slate-100"
            >
              <p className="text-sm text-slate-700 whitespace-pre-wrap">
                {note.body}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                {new Date(note.created_at).toLocaleString()}
              </p>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            value={noteBody}
            onChange={(e) => setNoteBody(e.target.value)}
            placeholder="Add a note..."
            className="flex-1 rounded border border-slate-300 px-3 py-1.5 text-sm"
          />
          <button
            onClick={handleAddNote}
            disabled={!noteBody.trim()}
            className="btn-primary text-sm flex items-center gap-1"
          >
            <Plus size={14} /> Add
          </button>
        </div>
      </div>
    </div>
  );
}